import commonUtil from "../../../util/common-utils";
import { Actions } from "../../../constants/actions";

export interface SearchCategoryNode {
    id: string;
    name: string;
    productsCount?: number | null;
}

export interface SearchCategoryTree {
    parents: SearchCategoryNode[];
    childrenByParentId: Map<string, SearchCategoryNode[]>;
}

export interface SearchCategoryModalRenderParams {
    listRoot: HTMLElement;
    tree: SearchCategoryTree;
    selectedIds: Set<string>;
    filterText?: string;
    showAll?: boolean;
}

type CategoryCountValue = number | null | undefined;

function toNonNegativeIntOrNull(raw: CategoryCountValue): number | null {
    if (raw === null || raw === undefined) return null;
    return Number.isFinite(raw) ? Math.max(0, Math.trunc(raw)) : null;
}

function createCategoryRows(): SearchCategoryNode[] {
    return [];
}

export class SearchCategoryModalRender {
    public static clearList(listRoot: HTMLElement): void {
        commonUtil.setHtml(listRoot, "");
    }

    public static renderAllOpen(params: SearchCategoryModalRenderParams): void {
        const { listRoot, tree, selectedIds, filterText, showAll } = params;
        const shouldShowAll = showAll !== false;
        const query = String(filterText || "")
            .trim()
            .toLowerCase();

        const parents = Array.isArray(tree?.parents) ? tree.parents : [];
        const sections = parents
            .map((parent) => {
                const parentId = String(parent.id);
                const parentName = String(parent.name || parentId);
                const children = tree.childrenByParentId.get(parentId) || [];
                const rows = createCategoryRows();

                if (children.length === 0 && parentId) {
                    rows.push({ id: parentId, name: parentName });
                }

                children.forEach((child) => {
                    const childId = String(child.id);
                    if (!childId || childId === parentId) return;
                    rows.push({
                        id: childId,
                        name: String(child.name || childId),
                        productsCount: toNonNegativeIntOrNull(child.productsCount),
                    });
                });

                const filteredByCount = shouldShowAll
                    ? rows
                    : rows.filter((row) => row.productsCount === null || row.productsCount > 0);

                const visibleRows = !query
                    ? filteredByCount
                    : filteredByCount.filter((row) =>
                          String(row.name || "")
                              .toLowerCase()
                              .includes(query),
                      );

                const shouldRenderSection = query
                    ? visibleRows.length > 0 || String(parentName).toLowerCase().includes(query)
                    : true;
                if (!shouldRenderSection) return "";

                const anySelected = rows.length > 0 && rows.some((row) => selectedIds.has(String(row.id)));
                const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(String(row.id)));
                const isIndeterminate = anySelected && !allSelected;
                const selectionState = allSelected ? "checked" : isIndeterminate ? "indeterminate" : "none";

                const buttons =
                    visibleRows.length > 0
                        ? visibleRows
                              .map((row) => {
                                  const isSelected = selectedIds.has(String(row.id));
                                  const selectedAttrs = isSelected
                                      ? ' data-selected="1" aria-pressed="true"'
                                      : ' aria-pressed="false"';
                                  const selectedClass = isSelected ? " search-category-option--selected" : "";

                                  const count = toNonNegativeIntOrNull(row.productsCount);
                                  const hasCount = count !== null;
                                  const isZero = hasCount && count === 0;
                                  const zeroClass = isZero ? " search-picker-option--zero" : "";

                                  const countHtml = hasCount
                                      ? `<span class="search-picker-option__count" aria-label="count">${commonUtil.escapeHtml(
                                            String(count),
                                        )}</span>`
                                      : "";

                                  return `<button type="button" class="search-category-option${selectedClass}${zeroClass}" data-action="${Actions.SELECT_CATEGORY}" data-category-id="${commonUtil.escapeHtmlAttribute(
                                      row.id,
                                  )}"${selectedAttrs}><span class="search-picker-option__name">${commonUtil.escapeHtml(
                                      row.name,
                                  )}</span>${countHtml}</button>`;
                              })
                              .join("")
                        : '<div class="search-category-modal__section-empty" data-translate="empty">-</div>';

                return `
					<section class="search-category-modal__section">
						<div class="search-category-modal__section-head">
                            <button type="button" class="section-title modal section-title--action" data-action="${Actions.SELECT_PARENT}" data-parent-id="${commonUtil.escapeHtmlAttribute(
                                parentId,
                            )}" data-selection-state="${selectionState}" ${isIndeterminate ? 'data-indeterminate="1"' : ""} ${allSelected ? 'aria-pressed="true"' : 'aria-pressed="false"'}>
								${commonUtil.escapeHtml(parentName)}
							</button>
						</div>
						<div class="search-category-modal__section-list">${buttons}</div>
					</section>
				`;
            })
            .filter(Boolean)
            .join("");

        commonUtil.setHtml(listRoot, sections);
    }
}
